import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/hooks/use-toast'
import {
  getActivityColor,
  getActivityDescription,
  getActivityIcon,
  getActivityTargetPath,
} from '@/lib/activity-feed'
import apiClient from '@/lib/apiClient'
import { cn } from '@/lib/utils'
import type { Activity } from '@/types/api'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Eye } from 'lucide-react'
import React from 'react'
import { useNavigate } from 'react-router-dom'

const ActivityRowSkeleton = () => (
  <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
    <div className="flex items-center space-x-3">
      <Skeleton className="h-10 w-10 rounded-full" />
      <div>
        <Skeleton className="mb-2 h-4 w-64" />
        <Skeleton className="h-3 w-32" />
      </div>
    </div>
    <Skeleton className="h-8 w-8" />
  </div>
)

const ActivitiesPage: React.FC = () => {
  const navigate = useNavigate()
  const { toast } = useToast()

  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['activities', 'page'],
    queryFn: () => apiClient.getActivities({ limit: 100 }),
  })

  const activities = response?.data?.activities || []

  React.useEffect(() => {
    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to load activity log',
        variant: 'destructive',
      })
    }
  }, [error, toast])

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          <h1 className="mt-3 text-3xl font-bold">Activity Log</h1>
          <p className="text-muted-foreground">Recent administrative activity across the system</p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Activities</CardTitle>
          <CardDescription>
            Showing the latest 100 activities captured by the admin backend.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {isLoading ? (
              <>
                <ActivityRowSkeleton />
                <ActivityRowSkeleton />
                <ActivityRowSkeleton />
              </>
            ) : activities.length > 0 ? (
              activities.map((activity: Activity) => {
                const targetPath = getActivityTargetPath(activity)

                return (
                  <div
                    key={activity.id}
                    className={cn(
                      'flex items-center rounded-lg border border-gray-200 p-4 transition-colors',
                      targetPath ? 'cursor-pointer hover:bg-gray-50' : 'bg-white'
                    )}
                    onClick={() => {
                      if (targetPath) {
                        navigate(targetPath)
                      }
                    }}
                  >
                    <div className="flex min-w-0 flex-1 items-center space-x-3">
                      <div
                        className={cn(
                          'rounded-full p-2.5 flex-shrink-0',
                          getActivityColor(activity.action)
                        )}
                      >
                        {getActivityIcon(activity.action)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {getActivityDescription(activity)}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                          <span>by {activity.admin.name}</span>
                          <span>{activity.time_ago}</span>
                          {activity.resource_type && <span>{activity.resource_type}</span>}
                        </div>
                      </div>
                    </div>
                    {targetPath && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(event) => {
                          event.stopPropagation()
                          navigate(targetPath)
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )
              })
            ) : (
              <div className="py-10 text-center text-muted-foreground">No activities found.</div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default ActivitiesPage
